import { History } from 'history';
import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Titled } from 'react-titled';
import GraphQLProvider from 'utils/graphql/Provider';
import GlobalStyle from 'components/global/GlobalStyle';
import { PageError } from 'components/common/PageError';
import ErrorBoundary from 'components/common/ErrorBoundary';
import Router from './Router';
import Routes from './routes';
import { local } from 'utils/storage';
import config, { ENABLE_MOCKS } from 'config';

import { MockAuth0Provider } from 'utils/auth/MockAuth0Provider';
import { Auth0Provider } from '@auth0/auth0-react';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import 'styles/fonts.css';
import 'styles/app.css';
import { MobileLeftNavProvider } from 'context/MobileLeftNav';

import { ThemeProvider } from '@resideo/blueprint-react';
import colors from 'themes/light';
import useScript from 'hooks/useScript';

export const AuthProvider = ({ children }: { children: any }) => {
  const history = useHistory();
  const onRedirectCallback = appState => {
    const publicUrl = `${process.env.PUBLIC_URL ?? '/'}`;
    let histReplace = '';
    if (appState?.returnTo === publicUrl) histReplace = '/';
    else
      histReplace =
        appState?.returnTo?.replace(publicUrl, '') || window.location.pathname;
    history.replace(histReplace);
  };

  const redirectUrl = `${window.location.origin}${process.env.PUBLIC_URL}`;

  if (ENABLE_MOCKS) {
    return <MockAuth0Provider>{children}</MockAuth0Provider>;
  } else {
    return (
      <Auth0Provider
        audience={config.audience}
        domain={config.domain}
        clientId={config.clientId}
        onRedirectCallback={onRedirectCallback}
        redirectUri={redirectUrl}
        scope={config.scope}
        useRefreshTokens={true}>
        {children}
      </Auth0Provider>
    );
  }
};

const App: FC<{ history?: History }> = ({ history }) => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const prefLocale = local.getItem('pref-locale');
    if (prefLocale && prefLocale !== 'en') {
      i18n.changeLanguage(prefLocale);
    }
  }, [i18n]);

  useScript('https://js.digitalriver.com/v1/DigitalRiver.js', true, false);
  useScript('https://www.paypalobjects.com/api/checkout.js', true, false);

  return (
    <Router history={history}>
      <AuthProvider>
        <ThemeProvider theme={colors}>
          <ErrorBoundary handler={PageError}>
            <GraphQLProvider>
              <MobileLeftNavProvider>
                <Titled title={() => t('title')}>
                  <GlobalStyle />
                  <Routes />
                </Titled>
              </MobileLeftNavProvider>
            </GraphQLProvider>
          </ErrorBoundary>
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
